import { useEffect, useMemo, useState } from 'react';

import {
  useOutreachSenders,
  useTestSendCampaign,
} from '../../hooks/useOutreach';
import type { CampaignRecipient } from '../../types/outreach';
import { useAuthUser } from '../../auth/useAuthUser';
import { extractApiMessage } from '../../api/client';
import styles from '../pages/OutreachPage.module.css';


const INTERNAL_DOMAIN_SUFFIX = '@ember-infra.com';


/**
 * Modal: pick a test recipient (internal-only) and send a one-off render
 * of the campaign's chosen step to them. Defaults to the current user's
 * email + step 1.
 *
 * Two callers:
 *   - Recipients tab: `recipient` is the row clicked; no picker; step 1.
 *   - Steps tab:      `recipient` defaults to the first eligible row,
 *                     `recipientOptions` provides the full list (picker),
 *                     `initialStepOrder` selects the step to render.
 *
 * The test send always inserts a campaign_sends row flagged is_test=1
 * (excluded from default Stats), and never advances the recipient state
 * or writes list statuses. Backend enforces the @ember-infra.com gate too.
 */
export function TestSendModal({
  campaignId,
  recipient,
  recipientOptions,
  initialStepOrder,
  onClose,
}: {
  campaignId: number;
  recipient: CampaignRecipient;
  /** When provided + non-empty, shows a recipient picker. */
  recipientOptions?: CampaignRecipient[];
  /** Step to render. Defaults to 1. */
  initialStepOrder?: number;
  onClose: () => void;
}) {
  const sendersQ = useOutreachSenders();
  const sendMut = useTestSendCampaign();
  const auth = useAuthUser();

  const [pickedRecipient, setPickedRecipient] =
    useState<CampaignRecipient>(recipient);
  const stepOrder = initialStepOrder ?? 1;
  const showRecipientPicker =
    !!recipientOptions && recipientOptions.length > 0;

  const defaultEmail =
    auth.email && auth.email.toLowerCase().endsWith(INTERNAL_DOMAIN_SUFFIX)
      ? auth.email
      : '';

  // If we don't have an internal session email (dev mode), start on
  // 'sender' so the modal isn't disabled out of the gate.
  const [mode, setMode] = useState<'me' | 'sender' | 'custom'>(
    defaultEmail ? 'me' : 'sender',
  );
  const [customEmail, setCustomEmail] = useState('');
  const [pickedSenderEmail, setPickedSenderEmail] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // Hydrate sender pick when senders land.
  useEffect(() => {
    if (sendersQ.data && !pickedSenderEmail && sendersQ.data.length > 0) {
      setPickedSenderEmail(sendersQ.data[0].email);
    }
  }, [sendersQ.data, pickedSenderEmail]);

  const internalSenders = useMemo(
    () =>
      (sendersQ.data || []).filter(
        (s) => s.email.toLowerCase().endsWith(INTERNAL_DOMAIN_SUFFIX),
      ),
    [sendersQ.data],
  );

  function resolveTestEmail(): string {
    if (mode === 'me') return defaultEmail;
    if (mode === 'sender') return pickedSenderEmail;
    return customEmail.trim();
  }

  async function handleSend() {
    setError(null);
    const email = resolveTestEmail().toLowerCase();
    if (!email) {
      setError('Pick a destination email.');
      return;
    }
    if (!email.endsWith(INTERNAL_DOMAIN_SUFFIX)) {
      setError(`Test recipient must be an internal address (${INTERNAL_DOMAIN_SUFFIX}).`);
      return;
    }
    try {
      const r = await sendMut.mutateAsync({
        campaignId,
        body: {
          recipient_id: pickedRecipient.id,
          test_email: email,
          step_order: stepOrder,
        },
      });
      alert(r.message || `Test sent to ${r.sent_to}.`);
      onClose();
    } catch (e: unknown) {
      setError(extractApiMessage(e, 'Test send failed.'));
    }
  }

  function recipientLabel(r: CampaignRecipient): string {
    const name = [r.first_name, r.last_name].filter(Boolean).join(' ');
    return name ? `${name} (${r.email})` : r.email;
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF', padding: 20, borderRadius: 8,
          minWidth: 460, maxWidth: 560,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 600 }}>
          Send test of step {stepOrder}
        </h3>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 16 }}>
          Renders against{' '}
          <strong>
            {[pickedRecipient.first_name, pickedRecipient.last_name]
              .filter(Boolean).join(' ') || pickedRecipient.email}
          </strong>
          's merge fields and routes to the chosen internal address.
          No status write-back, no recipient state change.
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {showRecipientPicker && (
          <div style={{ marginBottom: 12 }}>
            <label className={styles.label} style={{ marginBottom: 4 }}>
              Render against recipient
            </label>
            <select
              className={styles.input}
              value={pickedRecipient.id}
              onChange={(e) => {
                const next = recipientOptions!.find(
                  (r) => r.id === Number(e.target.value),
                );
                if (next) setPickedRecipient(next);
              }}
              style={{ width: '100%' }}
            >
              {recipientOptions!.map((r) => (
                <option key={r.id} value={r.id}>
                  {recipientLabel(r)}
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              cursor: defaultEmail ? 'pointer' : 'not-allowed',
              opacity: defaultEmail ? 1 : 0.5,
              fontSize: 13,
            }}
          >
            <input
              type="radio"
              name="ts-mode"
              checked={mode === 'me'}
              onChange={() => setMode('me')}
              disabled={!defaultEmail}
            />
            <span>
              Send to me{' '}
              <code style={{ color: '#555', fontSize: 12 }}>
                ({defaultEmail || 'no internal email on session'})
              </code>
            </span>
          </label>

          <label
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              cursor: 'pointer', fontSize: 13,
            }}
          >
            <input
              type="radio"
              name="ts-mode"
              checked={mode === 'sender'}
              onChange={() => setMode('sender')}
              disabled={internalSenders.length === 0}
            />
            <span>Send to a registered sender:</span>
            <select
              className={styles.input}
              value={pickedSenderEmail}
              onChange={(e) => setPickedSenderEmail(e.target.value)}
              disabled={mode !== 'sender'}
              style={{ flex: 1 }}
            >
              {internalSenders.map((s) => (
                <option key={s.email} value={s.email}>
                  {s.email}
                </option>
              ))}
            </select>
          </label>

          <label
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              cursor: 'pointer', fontSize: 13,
            }}
          >
            <input
              type="radio"
              name="ts-mode"
              checked={mode === 'custom'}
              onChange={() => setMode('custom')}
            />
            <span>Other internal address:</span>
            <input
              className={styles.input}
              type="email"
              value={customEmail}
              onChange={(e) => setCustomEmail(e.target.value)}
              placeholder={`name${INTERNAL_DOMAIN_SUFFIX}`}
              disabled={mode !== 'custom'}
              style={{ flex: 1 }}
            />
          </label>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
          <button type="button" className={styles.button} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={handleSend}
            disabled={sendMut.isPending}
          >
            {sendMut.isPending ? 'Sending…' : 'Send test'}
          </button>
        </div>
      </div>
    </div>
  );
}
